import React from 'react';
import { CalendarDaysIcon, FileSpreadsheetIcon, LayoutDashboardIcon, Settings2Icon, ShieldCheckIcon, UsersIcon, XIcon } from 'lucide-react';
import type { IconComponent } from '../../types/ui';
import { BrandLogo } from '../ui/BrandLogo';
export type NavSection = 'dashboard' | 'employees' | 'timeoff' | 'reports' | 'settings';
interface SidebarProps {
  active: NavSection;
  pendingCount: number;
  mobileOpen: boolean;
  onClose: () => void;
  onNavigate: (section: NavSection) => void;
}
const NAV: {
  id: NavSection;
  label: string;
  icon: IconComponent;
}[] = [{
  id: 'dashboard',
  label: 'Dashboard',
  icon: LayoutDashboardIcon
}, {
  id: 'employees',
  label: 'Employees',
  icon: UsersIcon
}, {
  id: 'timeoff',
  label: 'Time Off',
  icon: CalendarDaysIcon
}, {
  id: 'reports',
  label: 'Reports',
  icon: FileSpreadsheetIcon
}, {
  id: 'settings',
  label: 'Settings & Policies',
  icon: Settings2Icon
}];
export function Sidebar({
  active,
  pendingCount,
  mobileOpen,
  onClose,
  onNavigate
}: SidebarProps) {
  return <>
      {mobileOpen && <div className="fixed inset-0 z-30 bg-navy/40 backdrop-blur-sm lg:hidden" onClick={onClose} aria-hidden="true" data-id="element-64" data-mp="1w1njnj0rgjno" />}
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-72 flex-col overflow-y-auto bg-navy px-5 pb-6 pt-6 transition-transform duration-300 lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`} aria-label="Primary" data-id="element-65" data-mp="1r7j8soodjrhb">
        <button type="button" onClick={onClose} className="absolute right-3 top-3 rounded-lg p-1.5 text-white/60 hover:bg-white/10 hover:text-white lg:hidden" aria-label="Close navigation" data-id="element-66" data-mp="2ey5g2dqk17q9">
          <XIcon className="h-5 w-5" data-id="element-67" data-mp="20zfhsus0120b" />
        </button>

        <div data-id="element-68" data-mp="20yw1fihzyloe">
          <div className="flex h-16 items-center justify-center rounded-2xl bg-white px-5 shadow-lg shadow-black/20" data-id="element-69" data-mp="2119xnuoey3xj">
            <BrandLogo src="https://madisonmortgage.com/clients/8165/uploads/2020/11/Madison-Mortgage-Logo-2.png" className="h-9 w-auto object-contain" wordmarkClassName="text-lg" data-id="element-70" data-mp="pk0fdbh0thxt" />
          </div>
          <p className="mt-3 text-center text-[10px] font-semibold uppercase tracking-[0.28em] text-white/55" data-id="element-71" data-mp="vzqqcdozvbz4">HR Command Center</p>
        </div>

        <nav className="mt-10 flex-1" data-id="element-72" data-mp="m22u1jbqpweu">
          <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/35" data-id="element-73" data-mp="m11g973cavtf">Workspace</p>
          <ul className="mt-3 space-y-1" data-id="element-74" data-mp="89whs8bpk9ko">
            {NAV.map(({
            id,
            label,
            icon: Icon
          }) => {
            const isActive = id === active;
            return <li key={id} data-id="element-75" data-mp="3uinweh9v9r8z">
                  <button type="button" onClick={() => onNavigate(id)} aria-current={isActive ? 'page' : undefined} className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? 'bg-white/10 text-white' : 'text-white/65 hover:bg-white/[0.06] hover:text-white'}`} data-id="element-76" data-mp="3ujh69r44gwvc">
                    {isActive && <span className="absolute -left-5 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-flame" aria-hidden="true" data-id="element-77" data-mp="31wptkgciceg1" />}
                    <Icon className={`h-[18px] w-[18px] ${isActive ? 'text-azure' : 'text-white/50 group-hover:text-white/80'}`} aria-hidden="true" data-id="element-78" data-mp="3ft3393asvbjy" />
                    <span className="flex-1 text-left" data-id="element-79" data-mp="3psbqzef2ntxv">{label}</span>
                    {id === 'timeoff' && pendingCount > 0 && <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-flame px-1.5 text-[11px] font-semibold text-white" data-id="element-80" data-mp="3ku0ib70ijph8">
                        {pendingCount}
                      </span>}
                  </button>
                </li>;
          })}
          </ul>
        </nav>

        <div className="rounded-2xl bg-white/[0.06] p-4 ring-1 ring-inset ring-white/10" data-id="element-81" data-mp="7fw1p95ovhv5">
          <div className="flex items-center gap-2 text-xs font-semibold text-white" data-id="element-82" data-mp="21vue1n9567cw">
            <ShieldCheckIcon className="h-4 w-4 text-azure" aria-hidden="true" data-id="element-83" data-mp="26up85lck7icb" />
            Sample data only
          </div>
          <p className="mt-1.5 text-[11px] leading-relaxed text-white/55" data-id="element-84" data-mp="2bl5mr8s49a09">All people, numbers and documents shown here are fictional demo records.</p>
        </div>

        <div className="mt-5 flex flex-col items-center gap-1.5" data-id="element-85" data-mp="2fottxqcu63kz">
          <BrandLogo src="https://madisonmortgage.com/clients/8165/uploads/2020/04/footer-logo.png" className="h-7 w-auto object-contain opacity-70" wordmarkClassName="rounded-md bg-white px-2 py-0.5 text-xs opacity-70" data-id="element-86" data-mp="1iyb48hev0kpf" />
          <p className="text-[10px] tracking-wide text-white/40" data-id="element-87" data-mp="1wwrlv3e9fvmc">v1 · HR admins</p>
        </div>
      </aside>
    </>;
}